import { useCallback } from "react";

import { isCaseCopilotEnabled } from "src/cases/copilot/isCaseCopilotEnabled";
import type { CaseDeserialized, CaseTask } from "src/cases/queries/casesTypes";
import { TaskIdExtractor } from "src/cases/queries/casesTypes";
import {
  useApproveTaskFieldHint,
  useApproveTaskHint,
  useRejectTaskFieldHint,
  useRejectTaskHint,
  useTaskFieldHints,
} from "src/cases/queries/hints";
import type {
  ActionTriggeredEventDataActionFormDataValue,
  Value,
} from "src/clients/case-management";

type UseTaskFieldHintActionsParams = {
  caseData?: CaseDeserialized;
  task: CaseTask;
};

export const useTaskFieldHintActions = ({
  caseData,
  task,
}: UseTaskFieldHintActionsParams) => {
  const caseId = caseData?.data.id;

  const { data: fieldHints } = useTaskFieldHints(caseId, task.id, {
    enabled: isCaseCopilotEnabled(caseData),
  });

  const { mutate: approveTaskFieldHint, isPending: isApprovingHint } =
    useApproveTaskFieldHint();
  const { mutateAsync: approveTaskHint } = useApproveTaskHint();
  const { mutate: rejectTaskFieldHint, isPending: isRejectingHint } =
    useRejectTaskFieldHint();
  const { mutateAsync: rejectTaskHint } = useRejectTaskHint();

  const hasHints = fieldHints != null && Object.keys(fieldHints).length > 0;

  const handleApproveField = (fieldKey: string) => {
    if (!caseId || task.formMeta?.etag == null) return;
    const taskKey = TaskIdExtractor.getStatusAndTaskKey(task.id);
    approveTaskFieldHint({
      caseId,
      taskKey,
      fieldKey,
      hintId: task.id,
      ifMatch: task.formMeta.etag.toString(),
      value: (fieldHints?.[fieldKey] ?? null) as Value | null,
    });
  };

  const handleRejectField = (fieldKey: string) => {
    if (!caseId || isRejectingHint) return;
    rejectTaskFieldHint({
      caseId,
      taskKey: TaskIdExtractor.getStatusAndTaskKey(task.id),
      fieldKey,
    });
  };

  const approveAllHints = useCallback(async () => {
    if (!caseId || !fieldHints || task.formMeta?.etag == null) {
      throw new Error("Cannot approve hints: missing required data");
    }
    const response = await approveTaskHint({
      caseId,
      taskKey: TaskIdExtractor.getStatusAndTaskKey(task.id),
      hintId: task.id,
      ifMatch: task.formMeta.etag.toString(),
      attributes: fieldHints as Record<
        string,
        ActionTriggeredEventDataActionFormDataValue
      >,
    });
    const newEtag = (response.data.meta as { etag: string } | undefined)?.etag;
    return newEtag != null ? Number(newEtag) : undefined;
  }, [caseId, fieldHints, task, approveTaskHint]);

  const rejectAllHints = useCallback(async () => {
    if (!caseId) return;
    await rejectTaskHint({
      caseId,
      taskKey: TaskIdExtractor.getStatusAndTaskKey(task.id),
    });
  }, [caseId, rejectTaskHint, task.id]);

  return {
    fieldHints,
    hasHints,
    isApprovingHint,
    isRejectingHint,
    handleApproveField,
    handleRejectField,
    approveAllHints,
    rejectAllHints,
  };
};
